"use client";
import { HomeIcon, Search, User } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useBottomNavData } from "@/hooks/useBottomNavData";
import { useScrollHidden } from "@/hooks/useScrollHidden";
import { useAppData } from "@/store/appStore";
import { ReactNode, useCallback } from "react";

interface BottomNavComponentProps {
	loading?: boolean;
}

interface BottomNavItemProps {
	id: string;
	href?: string;
	label: string;
	icon: ReactNode;
	isActive: boolean;
	isLoading: boolean;
	onClick?: () => void;
}

const BottomNavItem = ({
	id,
	href,
	label,
	icon,
	isActive,
	isLoading,
	onClick,
}: BottomNavItemProps) => {
	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center gap-0.5 w-[60px] h-[60px] bg-[rgba(4,4,4,0.1)] rounded-[20px]">
				<div className="h-6 w-6 bg-gray-200 rounded animate-pulse" />
				<span className="text-[13px] leading-[1.5] font-lexend font-normal min-h-[20px] block opacity-0 transition-all duration-300">
					{label}
				</span>
			</div>
		);
	}

	const buttonContent = (
		<button
			className={cn(
				"flex flex-col items-center justify-center gap-0.5 relative w-[60px] h-[60px] rounded-[20px] transition-all duration-300",
				isActive ? "am-accent-fill rounded-2xl" : "bg-[rgba(4,4,4,0.1)]",
			)}
			onClick={onClick}
		>
			{icon}
			<span
				className={cn(
					"text-[13px] leading-[1.5] font-lexend font-normal min-h-[20px] block transition-all duration-300",
					isActive
						? "text-automint-text opacity-100"
						: "text-[#525252] opacity-0",
				)}
			>
				{label}
			</span>
		</button>
	);

	// If onClick is provided, render without Link wrapper
	if (onClick) {
		return <div className="contents">{buttonContent}</div>;
	}

	// Otherwise, wrap with Link
	return (
		<Link id={id} href={href || "#"} className="contents">
			{buttonContent}
		</Link>
	);
};

export default function BottomNav({ loading }: BottomNavComponentProps = {}) {
	const { navigationProduct, isReady } = useBottomNavData();
	const [appData] = useAppData();
	const pathname = usePathname();
	const pathSegments = pathname.split("/").filter(Boolean);

	const isLoading = loading || !isReady || !navigationProduct;

	const homePage = (route: string) =>
		(pathname?.startsWith(route) && pathSegments?.length === 0) ||
		pathname === "/";
	const carVariantPage =
		pathSegments[0] === "product" && pathSegments?.length === 2; // /product/[slug]
	// const leaseCalculatorPage =
	// 	pathSegments[0] === "product" && pathSegments?.length === 3; // /product/[slug]/[id]
	const profilePage = pathname?.startsWith("/profile");

	const isHomeActive = homePage("/");
	const isSearchActive = carVariantPage;
	// const isCalculatorActive = leaseCalculatorPage;
	const isProfileActive = profilePage;

	// Handle Search button toggle behavior
	const handleSearchClick = useCallback(() => {
		if (isSearchActive) {
			// Find and click the search trigger that's already on the page
			const searchTrigger = document.querySelector("#search") as HTMLElement;
			if (searchTrigger) {
				searchTrigger.click();
			}
		}
	}, [isSearchActive]);

	const isHidden = useScrollHidden();

	// Hide bottom nav on onboarding page and the standalone /game overlay
	if (pathname?.startsWith("/onboarding") || pathname?.startsWith("/game")) {
		return null;
	}

	return (
		<footer
			className={cn(
				"am-chrome",
				"relative w-full fixed bottom-0 z-20 md:hidden",
				"transition-transform duration-300 ease-out will-change-transform",
				isHidden && "translate-y-full",
			)}
		>
			{/* Drawn only by themes that define it — see components.css */}
			<div className="am-chrome-hairline absolute inset-x-0 top-0" />
			<nav className="flex justify-around items-center px-4 py-2">
				{/* Home */}
				<BottomNavItem
					id="bottom_navigation_home"
					href="/"
					label="Home"
					icon={
						<HomeIcon
							size={20}
							className={cn(
								"transition-colors duration-300",
								isHomeActive ? "text-automint-text" : "text-[#525252]",
							)}
						/>
					}
					isActive={isHomeActive}
					isLoading={isLoading}
				/>

				{/* Search */}
				<BottomNavItem
					id="bottom_navigation_search"
					href={
						isSearchActive
							? undefined
							: `/product/${navigationProduct?.manufacturer};${navigationProduct?.model}`
					}
					label="Search"
					icon={
						<Search
							size={20}
							className={cn(
								"transition-colors duration-300",
								isSearchActive ? "text-automint-text" : "text-[#525252]",
							)}
						/>
					}
					isActive={isSearchActive}
					isLoading={isLoading}
					onClick={isSearchActive ? handleSearchClick : undefined}
				/>

				{/* Calculator */}
				{/* <BottomNavItem
					id="bottom_navigation_calculator"
					href={`/product/${navigationProduct?.manufacturer};${navigationProduct?.model}/${navigationProduct?.id}`}
					label="Calculator"
					icon={
						<Calculator
							size={20}
							className={cn(
								"transition-colors duration-300",
								isCalculatorActive ? "text-automint-text" : "text-[#525252]",
							)}
						/>
					}
					isActive={isCalculatorActive}
					isLoading={isLoading}
				/> */}

				{/* Profile */}
				{isLoading ? (
					<div className="flex flex-col items-center justify-center gap-0.5 w-[60px] h-[60px] bg-[rgba(4,4,4,0.1)] rounded-[20px]">
						<div className="h-6 w-6 bg-gray-200 rounded-full animate-pulse border-2 border-automint-neon" />
						<span className="text-[13px] leading-[1.5] font-lexend font-normal min-h-[20px] block opacity-0 transition-all duration-300">
							Profile
						</span>
					</div>
				) : (
					<Link
						id="bottom_navigation_profile"
						href="/profile"
						className="contents"
					>
						<button
							className={cn(
								"flex flex-col items-center justify-center gap-0.5 relative w-[60px] h-[60px] rounded-[20px] transition-all duration-300",
								isProfileActive
									? "am-accent-fill rounded-2xl"
									: "bg-[rgba(4,4,4,0.1)]",
							)}
						>
							{appData?.user ? (
								<div className="flex justify-center items-center w-6 h-6 rounded-full border-2 border-automint-neon bg-gray-900">
									<span className="text-automint-neon text-xs font-medium">
										{appData?.user?.firstName && appData?.user?.lastName
											? `${appData.user.firstName.charAt(0).toUpperCase()}${appData.user.lastName.charAt(0).toUpperCase()}`
											: appData?.user?.firstName
												? appData.user.firstName.substring(0, 2).toUpperCase()
												: "U"}
									</span>
								</div>
							) : (
								<User
									size={20}
									className={cn(
										"transition-colors duration-300",
										isProfileActive ? "text-automint-text" : "text-[#525252]",
									)}
								/>
							)}
							<span
								className={cn(
									"text-[13px] leading-[1.5] font-lexend font-normal min-h-[20px] block transition-all duration-300",
									isProfileActive
										? "text-automint-text opacity-100"
										: "text-[#525252] opacity-0",
								)}
							>
								Profile
							</span>
						</button>
					</Link>
				)}
			</nav>
		</footer>
	);
}
